<script setup lang="ts">
import { ref, computed, shallowRef, nextTick, watch, useTemplateRef } from 'vue'

import { useIntersectionObserver, useResizeObserver, whenever } from '@vueuse/core'
import { deepGet } from 'ts-deep-utils'
import { useI18n } from 'vue-i18n'

import { getIntlLocales } from '@/utils/format'

import type { Ref } from 'vue'
import type { ValidationRule } from 'vuetify'
import type { Density } from '@/types/_vuetify'

import ListItemContent from './ListItemContent.vue'

type ListItem = Record<string, unknown>

export interface FilterChipOption {
  text: string
  value: string | number
}

const props = withDefaults(
  defineProps<{
    items: ListItem[]
    itemTitle?: string | ((item: ListItem) => string)
    itemValue?: string
    itemBadge?: string
    itemTitleClass?: string
    label?: string
    placeholder?: string
    error?: boolean
    errorMessages?: string | string[]
    multiple?: boolean
    clearable?: boolean
    autofocus?: boolean
    disabled?: boolean
    hideDetails?: boolean | 'auto'
    density?: Density
    loading?: boolean
    /** When true, internal filtering is disabled. The parent should filter items based on the search model. */
    noFilter?: boolean
    /** Optional list of filter chips to display between search field and "toggle select all" */
    filterChips?: FilterChipOption[]
    /**
     * Limit chip-row height to N rows when set. A "Show more / less" toggle is
     * rendered only when the chip list actually overflows the limit. Default
     * (undefined) preserves existing behaviour for all other consumers.
     */
    filterChipsMaxRows?: number
    /** When true, more filter chip pages can be fetched (paired with `filterChipsLoadMore` event). */
    filterChipsHasMore?: boolean
    /** When true, a fetch for more filter chips is in flight — used to gate further loadMore emits. */
    filterChipsLoading?: boolean
    /**
     * Minimum width applied to the inner `<v-select>` element and to the
     * dropdown overlay. Numbers are interpreted as pixels. Useful with
     * `hideInput` where the activator is custom but the menu still needs a
     * sensible floor width.
     */
    minWidth?: string | number
    /** When true (and noFilter is true), indicates there are more items to load */
    hasMore?: boolean
    hideInput?: boolean
    customSelectAll?: boolean
    /** Hides the "select all" row of a multi-select, for lists where all values are never loaded at once */
    hideSelectAll?: boolean
    totalItems?: number
    allSelected?: boolean
    excludedIds?: unknown[]
    readonly?: boolean
    rules?: ValidationRule[]
  }>(),
  {
    itemTitle: 'title',
    itemValue: 'value',
    placeholder: 'common.select',
    error: false,
    errorMessages: undefined,
    label: undefined,
    hideDetails: false,
    density: undefined,
    loading: false,
    noFilter: false,
    filterChips: undefined,
    filterChipsMaxRows: undefined,
    filterChipsHasMore: false,
    filterChipsLoading: false,
    minWidth: undefined,
    hasMore: false,
    itemBadge: undefined,
    itemTitleClass: undefined,
    hideInput: false,
    totalItems: undefined,
    customSelectAll: false,
    hideSelectAll: false,
    allSelected: undefined,
    excludedIds: undefined,
    readonly: undefined,
    rules: undefined,
  }
)

const emit = defineEmits<{
  loadMore: []
  filterChipsLoadMore: []
  selectAll: [selected: boolean]
}>()

const model = defineModel<unknown>()
const search = defineModel<string | null>('search', { default: '' })
const selectedFilterChips = defineModel<(string | number)[]>('selectedFilterChips', { default: () => [] })
const menuModel = defineModel<boolean>('menu')

const { t } = useI18n()

const prependItemRef = useTemplateRef<HTMLElement>('prependItem')
const selectRef = useTemplateRef<{ $el: HTMLElement }>('selectRef')
const searchFieldRef = useTemplateRef('searchFieldRef')
const loadMoreRef = useTemplateRef('loadMore')

const maxNumberOfChips = ref(2)
const maxMenuWidth = ref(0)
const menuOverlay = shallowRef<HTMLElement | null>(null)
const loadMoreVisible = shallowRef(false)

const selectAllTitle = computed(() => `${t('common.select')} ${t('common.all').toLocaleLowerCase(getIntlLocales())}`)
const effectiveTotalItems = computed(() => props.totalItems ?? props.items.length)

const selectionState = computed<'all' | 'some' | 'none'>(() => {
  if (props.allSelected != null) {
    if (props.allSelected) return 'all'
    if (Array.isArray(model.value) && model.value.length > 0) return 'some'
    return 'none'
  }

  if (Array.isArray(model.value)) {
    if (model.value.length === 0) return 'none'
    if (model.value.length >= effectiveTotalItems.value) return 'all'
    return 'some'
  }

  return model.value != null ? 'all' : 'none'
})
const searchable = computed(() => props.noFilter || props.items.length > 5)

function resolveItemTitle(item: ListItem): string {
  if (typeof props.itemTitle === 'function') return props.itemTitle(item)
  const value = deepGet(item, props.itemTitle)
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/**
 * Precomputed value→title map from the full unfiltered items list.
 * Used by the #selection slot to resolve chip titles even when items are filtered out by search.
 */
const titleByValue = computed(() => {
  const map = new Map<unknown, string>()
  for (const item of props.items) {
    map.set(deepGet(item, props.itemValue), resolveItemTitle(item))
  }
  return map
})

function resolveSelectionTitle(item: ListItem | string | number): string {
  if (typeof item === 'string' || typeof item === 'number') {
    return titleByValue.value.get(item) ?? String(item)
  }
  return resolveItemTitle(item)
}

const filteredItems = computed(() => {
  if (props.noFilter) return props.items

  if (search.value == null || search.value.trim() === '') return props.items

  const searchTerm = search.value.toLocaleLowerCase(getIntlLocales())
  return props.items.filter((item: ListItem) =>
    resolveItemTitle(item).toLocaleLowerCase(getIntlLocales()).includes(searchTerm)
  )
})
const amountOfSelectedValues = computed(() =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  Array.isArray(model.value) ? model.value?.length : model.value != null ? 1 : 0
)
const amountOfChips = computed(() => Math.min(amountOfSelectedValues.value, maxNumberOfChips.value))
const subtractionWidth = computed(
  () => `${amountOfSelectedValues.value > maxNumberOfChips.value ? 80 / amountOfChips.value : 10}px`
)
const hasFilterChips = computed(() => props.filterChips != null && props.filterChips.length > 0)

const resolvedMinWidth = computed(() =>
  props.minWidth == null ? undefined : typeof props.minWidth === 'number' ? `${props.minWidth}px` : props.minWidth
)

// Filter-chips row height limiting + overflow detection.
// Only relevant when `filterChipsMaxRows` is set. Rather than predict chip
// height (which varies with size, label vs default, font, browser zoom), we
// count distinct chip `offsetTop` values to determine the row count and use
// the (N+1)th row's offsetTop as the precise clip-height.
const filterChipsExpanded = ref(false)
const chipsContainerRef = useTemplateRef<HTMLElement>('chipsContainer')
const isChipsOverflowing = ref(false)
const clipHeightPx = ref<number | null>(null)
const expandedClipHeightPx = ref<number | null>(null)
const naturalHeightPx = ref<number | null>(null)

// Cap the chip section's expanded height so the list-item area below always
// has room. If chips would render more than this many rows when expanded, we
// clip to exactly this many rows and let the section scroll internally.
const EXPANDED_CHIPS_MAX_ROWS = 4

// Returns a concrete px value when a measurement is available, else
// `undefined` to fall back to the CSS default. The CSS transition on
// max-height still animates between any two concrete px values.
const maxChipsHeight = computed(() => {
  if (filterChipsExpanded.value) {
    if (expandedClipHeightPx.value != null) return `${expandedClipHeightPx.value}px`
    if (naturalHeightPx.value != null) return `${naturalHeightPx.value}px`
    return undefined
  }
  return clipHeightPx.value != null ? `${clipHeightPx.value}px` : undefined
})

// Bump menu max-height when filter chips are present so chip-section + list
// items both have breathing room. Vuetify's default v-select menu max-height
// (~310px) is too tight once the chip section takes ~80–160px.
const mergedMenuProps = computed(() => (hasFilterChips.value ? { maxHeight: 480 } : undefined))

const showFilterChipsToggle = computed(
  () => props.filterChipsMaxRows != null && (isChipsOverflowing.value || filterChipsExpanded.value)
)

function checkChipsOverflow() {
  const el = chipsContainerRef.value
  if (el == null || props.filterChipsMaxRows == null) {
    isChipsOverflowing.value = false
    clipHeightPx.value = null
    expandedClipHeightPx.value = null
    naturalHeightPx.value = null
    return
  }
  // overflow:hidden doesn't change flex layout, so chip offsetTop values
  // reflect the natural row positions even when max-height is applied.
  const chips = Array.from(el.querySelectorAll<HTMLElement>('.v-chip'))
  if (chips.length === 0) {
    isChipsOverflowing.value = false
    clipHeightPx.value = null
    expandedClipHeightPx.value = null
    naturalHeightPx.value = null
    return
  }
  const rowTops = [...new Set(chips.map((chip) => chip.offsetTop))].sort((a, b) => a - b)
  const overflowing = rowTops.length > props.filterChipsMaxRows
  isChipsOverflowing.value = overflowing
  // When overflowing, clip exactly at the top of row N+1. Otherwise leave the
  // chip row at its natural height (no clipping when it already fits).
  clipHeightPx.value = overflowing ? (rowTops[props.filterChipsMaxRows] ?? null) : null
  // For the expanded state: if the chip list would render more than
  // EXPANDED_CHIPS_MAX_ROWS rows, clip at the top of that row (so exactly
  // N rows visible) and let overflowY:auto scroll the rest. If fewer rows,
  // no cap — but we still need a concrete px value (natural height) so the
  // CSS transition on max-height can animate from clipHeightPx to it.
  expandedClipHeightPx.value =
    rowTops.length > EXPANDED_CHIPS_MAX_ROWS ? (rowTops[EXPANDED_CHIPS_MAX_ROWS] ?? null) : null
  naturalHeightPx.value = el.scrollHeight
}

useResizeObserver(chipsContainerRef, checkChipsOverflow)

watch(
  () => props.filterChips,
  () => {
    void nextTick(checkChipsOverflow)
  },
  { deep: true }
)

watch(filterChipsExpanded, (expanded) => {
  if (!expanded) {
    // Reset scroll to the top so collapsed view always shows the first row of
    // chips, even if the user had scrolled inside the expanded list. Done
    // before re-measuring so checkChipsOverflow sees clientHeight from the
    // top-anchored content.
    if (chipsContainerRef.value != null) chipsContainerRef.value.scrollTop = 0
    void nextTick(checkChipsOverflow)
  }
})

watch(menuModel, async (open) => {
  if (open) {
    search.value = ''
    await nextTick()
    menuOverlay.value = prependItemRef.value?.closest<HTMLElement>('.v-overlay__content') ?? null

    // Apply explicit min-width if provided (e.g. for hideInput cases where the
    // activator is custom and the natural overlay width would be too narrow).
    if (resolvedMinWidth.value != null && menuOverlay.value != null) {
      menuOverlay.value.style.setProperty('--b-select-min-width', resolvedMinWidth.value)
    }

    // Otherwise, ensure the dropdown is at least as wide as the input field
    if (!props.hideInput && menuOverlay.value != null && selectRef.value != null) {
      const inputWidth = selectRef.value.$el.offsetWidth
      if (inputWidth > 0) {
        menuOverlay.value.style.setProperty('--b-select-min-width', `${inputWidth}px`)
        maxMenuWidth.value = Math.max(maxMenuWidth.value, inputWidth)
      }
    }

    // Focus the in-dropdown search so the user can type immediately.
    if (searchable.value) {
      setTimeout(() => searchFieldRef.value?.focus(), 100)
    }
  } else {
    // Only reset tracking state, don't remove the CSS variable — the overlay is still
    // animating out and removing min-width causes a visual width jump during the close animation.
    maxMenuWidth.value = 0
    menuOverlay.value = null
  }
})

function onMenuUpdate(state: boolean) {
  if (state) {
    search.value = ''
  }
}

function updateFilterChips(chipValues: (string | number | null)[]) {
  if (chipValues.includes(null)) {
    selectedFilterChips.value = []
  } else {
    selectedFilterChips.value = chipValues.filter((x) => x != null)
  }
}

function toggleSelectAll() {
  const selectingAll = selectionState.value === 'none'
  emit('selectAll', selectingAll)

  if (props.customSelectAll) {
    return
  }

  if (selectingAll) {
    model.value = props.items.map((item) => item[props.itemValue])
  } else {
    model.value = []
  }
}

function isItemSelected(item: ListItem) {
  const itemValue = item[props.itemValue]
  if (props.excludedIds != null) {
    return !props.excludedIds.includes(itemValue)
  }
  if (!Array.isArray(model.value)) return model.value != null
  return model.value.includes(itemValue)
}

function getItemBadge(item: ListItem): number | string | undefined {
  if (props.itemBadge == null) return undefined
  const value = item[props.itemBadge]
  if (typeof value === 'number' || typeof value === 'string') return value
  return undefined
}

function getItemTitleClass(item: ListItem): string | undefined {
  if (props.itemTitleClass == null) return undefined
  const value = item[props.itemTitleClass]
  if (typeof value === 'string') return value
  return undefined
}

function loadMoreIfNeeded() {
  if (!loadMoreVisible.value) return
  if (!props.hasMore) return
  if (props.loading) return
  emit('loadMore')
}

// Infinite scroll for filter chips. Sentinel sits at the bottom of the chip
// container and is observed against that container as root, so it only
// intersects once the user has actually scrolled the chip section to its end
// (i.e. expanded + > EXPANDED_CHIPS_MAX_ROWS rows scrolled to bottom).
const filterChipsLoadMoreRef = useTemplateRef<HTMLElement>('filterChipsLoadMore')
const filterChipsLoadMoreVisible = shallowRef(false)

useIntersectionObserver(
  filterChipsLoadMoreRef,
  ([entry]) => {
    filterChipsLoadMoreVisible.value = entry?.isIntersecting ?? false
  },
  { root: chipsContainerRef }
)

function loadMoreFilterChipsIfNeeded() {
  if (!filterChipsLoadMoreVisible.value) return
  if (!props.filterChipsHasMore) return
  if (props.filterChipsLoading) return
  emit('filterChipsLoadMore')
}

whenever(
  () => !props.filterChipsLoading,
  async () => {
    await nextTick()
    loadMoreFilterChipsIfNeeded()
  }
)

whenever(filterChipsLoadMoreVisible, () => {
  loadMoreFilterChipsIfNeeded()
})

useResizeObserver(menuOverlay, ([entry]) => {
  if (entry == null || menuOverlay.value == null) return
  const width = entry.contentRect.width
  if (width > maxMenuWidth.value) {
    maxMenuWidth.value = width
    menuOverlay.value.style.setProperty('--b-select-min-width', `${width}px`)
  }
})

useIntersectionObserver(loadMoreRef as unknown as Ref<HTMLElement>, ([entry]) => {
  loadMoreVisible.value = entry?.isIntersecting ?? false
})

whenever(
  () => !props.loading,
  async () => {
    await nextTick()
    loadMoreIfNeeded()
  }
)

whenever(loadMoreVisible, () => {
  loadMoreIfNeeded()
})
</script>

<template>
  <v-select
    ref="selectRef"
    v-model="model"
    v-model:menu="menuModel"
    :items="filteredItems"
    :multiple="props.multiple"
    :item-title="props.itemTitle"
    :item-value="props.itemValue"
    :label="props.label"
    :placeholder="t(props.placeholder)"
    :error="props.error"
    :error-messages="props.errorMessages"
    :clearable="props.clearable"
    :autofocus="props.autofocus"
    :disabled="props.disabled"
    :hide-details="props.hideDetails"
    :density="props.density"
    :loading="props.loading"
    :readonly="props.readonly"
    :rules="props.rules"
    :class="{ 'b-select--hidden-input': hideInput }"
    :style="resolvedMinWidth != null ? { minWidth: resolvedMinWidth } : undefined"
    :menu-props="mergedMenuProps"
    class="b-select"
    @update:menu="onMenuUpdate"
  >
    <template #prepend-item>
      <div
        ref="prependItem"
        class="b-select--sticky-prepend-item"
        :style="{ paddingTop: searchable ? '8px' : '0px' }"
      >
        <v-text-field
          v-if="searchable"
          ref="searchFieldRef"
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          hide-details
          clearable
          class="mb-2 mx-2"
          @pointerdown.stop
          @mousedown.stop
          @keydown.stop
        />

        <v-divider v-if="searchable" />

        <div
          v-if="hasFilterChips"
          class="b-select--chips-section px-2 d-flex flex-column ga-1"
        >
          <div
            ref="chipsContainer"
            class="b-select--chips-container d-flex flex-wrap ga-1"
            :style="
              maxChipsHeight != null
                ? { maxHeight: maxChipsHeight, overflowY: filterChipsExpanded ? 'auto' : 'hidden' }
                : {}
            "
          >
            <v-chip-group
              :model-value="selectedFilterChips"
              multiple
              column
              size="small"
              variant="outlined"
              @update:model-value="updateFilterChips($event)"
            >
              <v-chip
                :text="t('common.all')"
                :value="null"
                :variant="selectedFilterChips.length === 0 ? 'flat' : 'outlined'"
                :color="selectedFilterChips.length === 0 ? 'primary' : undefined"
                label
              />
              <v-chip
                v-for="chip in props.filterChips"
                :key="chip.value"
                :value="chip.value"
                :text="chip.text"
                :variant="selectedFilterChips.includes(chip.value) ? 'flat' : 'outlined'"
                :color="selectedFilterChips.includes(chip.value) ? 'primary' : undefined"
                label
              />
            </v-chip-group>
            <div
              v-if="props.filterChipsHasMore || props.filterChipsLoading"
              ref="filterChipsLoadMore"
              class="d-flex justify-center align-center w-100 py-1"
            >
              <v-progress-circular
                v-if="props.filterChipsLoading"
                size="16"
                width="2"
                indeterminate
                color="primary"
              />
            </div>
          </div>
          <v-btn
            v-if="showFilterChipsToggle"
            variant="text"
            size="small"
            :append-icon="filterChipsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            class="align-self-center w-100 mb-2"
            @click="filterChipsExpanded = !filterChipsExpanded"
          >
            {{ filterChipsExpanded ? t('common.show_less') : t('common.show_more') }}
          </v-btn>
        </div>

        <v-divider v-if="hasFilterChips" />

        <v-list-item
          v-if="props.multiple && !props.loading && !props.hideSelectAll"
          role="button"
          class="b-select--toggle-all clickable"
          @click="toggleSelectAll"
        >
          <list-item-content
            :model-value="selectionState !== 'none'"
            :indeterminate="selectionState === 'some'"
            :title="selectAllTitle"
            class="pl-2"
          />
        </v-list-item>

        <v-divider v-if="props.multiple && !props.loading && !props.hideSelectAll" />
      </div>
    </template>

    <template #selection="{ item, index }">
      <slot
        name="selection"
        :item="item"
        :index="index"
      >
        <!-- Default rendering for multiple mode -->
        <template v-if="props.multiple">
          <v-chip
            v-if="index < maxNumberOfChips"
            size="small"
            variant="outlined"
          >
            {{ resolveSelectionTitle(item) }}
          </v-chip>
          <v-chip
            v-else-if="index === maxNumberOfChips && Array.isArray(model)"
            size="small"
            variant="outlined"
          >
            +{{ model.length - maxNumberOfChips }}
          </v-chip>
        </template>
        <!-- Default rendering for single mode: just display the title -->
        <span v-else>{{ resolveSelectionTitle(item) }}</span>
      </slot>
    </template>

    <template #item="{ item, internalItem, props: itemProps }">
      <slot
        name="item"
        :item="item"
        :props="itemProps"
      >
        <!-- Default rendering for multiple mode with checkbox -->
        <v-list-item
          v-if="props.multiple"
          v-bind="itemProps"
          title=""
          class="b-select--list-item"
        >
          <list-item-content
            :model-value="isItemSelected(item)"
            :title="internalItem.title"
            :badge="getItemBadge(item)"
            :title-class="getItemTitleClass(item)"
            class="px-2"
          />
        </v-list-item>
        <!-- Default rendering for single mode -->
        <v-list-item
          v-else
          v-bind="itemProps"
        />
      </slot>
    </template>

    <!-- Vuetify renders #no-data whenever the list is empty, so the loading
         state is gated inside the slot rather than on it. The fixed keys on the
         closing rows keep Vue from reusing an append-item row as the no-data row. -->
    <template #no-data>
      <v-list-item key="no-data">
        <div
          v-if="props.loading"
          class="b-select--loading"
          role="status"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="32"
            width="3"
            aria-hidden="true"
          />
          <span class="text-secondary">{{ t('common.searching_for_results') }}</span>
        </div>
        <div
          v-else
          class="b-select--no-data"
        >
          <i class="text-secondary">{{ t('common.no_search_result') }}</i>
        </div>
      </v-list-item>
    </template>

    <template
      v-if="props.hasMore || props.loading"
      #append-item
    >
      <v-list-item
        v-if="props.loading && props.items.length > 0"
        key="load-more-spinner"
      >
        <div
          class="b-select--loading"
          role="status"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="24"
            width="3"
            aria-hidden="true"
          />
          <span class="text-secondary">{{ t('common.searching_for_results') }}</span>
        </div>
      </v-list-item>
      <!-- Infinite-scroll sentinel. v-show (not v-if) so the template ref stays
           mounted while loading; when it re-enters the viewport loadMoreIfNeeded
           fetches the next page. -->
      <v-list-item
        v-show="props.hasMore && !props.loading"
        ref="loadMore"
        key="load-more"
        density="compact"
        aria-hidden="true"
      />
    </template>
  </v-select>
</template>

<style scoped>
.b-select :deep(.v-select__selection) {
  max-width: calc(100% / v-bind(amountOfChips) - v-bind(subtractionWidth));
}
</style>

<style>
.b-select--wrapper {
  position: relative;
}

.b-select .v-select__selection .v-chip__content {
  display: inline-block !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.b-select--no-data {
  margin: 5px 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.b-select--loading {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
}

.b-select--hidden-input .v-field {
  visibility: hidden;
  height: 0;
}

.v-overlay__content:has(.b-select--sticky-prepend-item) {
  min-width: var(--b-select-min-width) !important;
}

.v-list:has(.b-select--sticky-prepend-item) {
  padding: 0;
}

.b-select--sticky-prepend-item {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
}

.b-select--list-item,
.b-select--toggle-all {
  padding: 0 !important;

  & .v-list-item__overlay {
    background: transparent !important;
  }
}

.b-select--toggle-all {
  background: #fafafa;
}

/* Prevent the filter-chip row from contributing to the overlay's intrinsic
 * width. Width 0 + min-width 100% makes the section take its parent's actual
 * width at render time, but contribute zero to min-content sizing. Result:
 * overlay width is driven by list items / search / select-all only, and the
 * chip row wraps against that width — which lets `filterChipsMaxRows`
 * overflow detection trigger naturally. */
.b-select--chips-section {
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
}

/* Smooth animation when the chip section toggles between clip-row and
 * expanded states. Matches Vuetify's standard 300ms transition timing. */
.b-select--chips-container {
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Outlined chips have a 1px border; flat chips have none. Add a transparent
 * 1px border to flat-variant chips inside the filter row so toggling between
 * states doesn't cause a 2px size jump. */
.b-select--chips-container .v-chip--variant-flat {
  border: 1px solid transparent;
}
</style>
